import { type ChangeEvent, forwardRef, useRef } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import { BaseInput } from '../_base-input/BaseInput.js';
import { BaseInputButton } from '../_base-input/BaseInputButton.js';
import {
  BaseInputSuffix,
  BaseInputPrefix,
} from '../_base-input/BaseInputContent.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';

const COMPONENT_NAME = 'TextInput';

// #region Typings
/**
 * @deprecated - please use TextInputProps instead.
 * @public
 */
export interface TextInputControlledProps {
  /** The default value of the input. */
  defaultValue?: never;
  /** The value of the input. */
  value?: string;
}

/**
 * @deprecated - please use TextInputProps instead.
 * @public
 */
export interface TextInputUncontrolledProps {
  /** The default value of the input. */
  defaultValue?: string;
  /** The value of the input. */
  value?: never;
}

/**
 * The properties of the TextInput component.
 * @public
 */
export type TextInputProps = Pick<
  BaseInputProps,
  | 'autoComplete'
  | 'placeholder'
  | 'variant'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'onClick'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onFocus'
  | 'onBlur'
  | 'onValidityChange'
  | 'role'
  | 'className'
  | 'style'
  | 'data-testid'
  | 'data-dtrum-mask'
  | 'data-dtrum-allow'
> &
  FormControlProps<
    string,
    (value: string, event: ChangeEvent<HTMLInputElement>) => void,
    HTMLInputElement
  > &
  WithChildren &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
    /**
     * Maps to the native input type. Use this to enhance the semantics
     * of the native input, change default keyboards on mobile or support browser
     * autocompletion for specific types.
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types}
     *
     * @defaultValue 'text'
     */
    type?: 'text' | 'password' | 'email' | 'url' | 'tel' | 'search';
  };
// #endregion

const TextInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  TextInputProps
>((props, forwardedRef) => {
  const {
    onChange,
    value,
    defaultValue,
    id: propId,
    name,
    required,
    readOnly,
    _form,
    _messageTarget,
    onValidityChange,
    type = 'text',
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  }: TextInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const [internalValue, setInternalValue] = useControllableState({
    defaultValue,
    value,
  });

  const controlRef = useRef<FormControlRef<HTMLDivElement>>(null);
  const mergedRef = useMergeRefs<FormControlRef<HTMLDivElement> | null>([
    forwardedRef,
    controlRef,
  ]);

  const formControl = useInternalFormRegistration({
    id: propId,
    name,
    form: _form,
    messageTarget: _messageTarget,
    value: internalValue ?? '',
    controlRef,
    onValidityChange,
  });

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value, e);
    setInternalValue(e.target.value);
  };

  return (
    <BaseInput
      ref={mergedRef}
      {...mergeProps(restProps, behavioralTrackingProps)}
      id={formControl.id}
      name={name}
      form={_form}
      type={type}
      value={internalValue}
      required={required}
      readOnly={readOnly}
      onValidityChange={formControl.onValidityChange}
      onChange={handleOnChange}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-disabled={ariaDisabledProp}
      data-dt-component={COMPONENT_NAME}
    />
  );
});

/**
 * Use the `TextInput` component to request a small amount of information,
 * such as a name or an email address. To let users input
 * larger amounts of text, use a `TextArea` instead.
 * @public
 */
export const TextInput = Object.assign(TextInputRoot, {
  Prefix: BaseInputPrefix,
  Suffix: BaseInputSuffix,
  Button: BaseInputButton,
});

(TextInput as typeof TextInput & { displayName: string }).displayName =
  COMPONENT_NAME;
